/**
 * Guard against reading back our own redaction tokens.
 *
 * `deepScrub` (sentryScrub.ts) used to rewrite its input in place, so the
 * redaction tokens it produces — "[EMAIL]", "[KBO]", "[JWT]", "[IBAN]" — were
 * written into the live company object that the app then persisted to
 * localStorage. That mutation is fixed, but a browser that cached a poisoned
 * object still reads it back on every load and renders "[KBO]" / "[EMAIL]"
 * where the customer's VAT number and email belong. Deploying the fix does not
 * heal those browsers; only distrusting the cache does.
 *
 * So: treat a cached value containing a redaction token as corrupt, drop it,
 * and let the caller fetch fresh data.
 */

const SCRUB_SENTINELS = ["[EMAIL]", "[KBO]", "[JWT]", "[IBAN]"] as const;

const MAX_DEPTH = 6;

export const SELECTED_COMPANY_CACHE_KEY = "selectedUserCompany";

export function containsScrubSentinel(value: unknown, depth = 0): boolean {
  if (depth > MAX_DEPTH) return false;
  if (typeof value === "string") return SCRUB_SENTINELS.some((token) => value.includes(token));
  if (Array.isArray(value)) return value.some((entry) => containsScrubSentinel(entry, depth + 1));
  if (value !== null && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some((entry) =>
      containsScrubSentinel(entry, depth + 1)
    );
  }
  return false;
}

/**
 * Returns the cached company, or null when there is nothing usable — which the
 * caller should treat as "fetch it fresh". Removes the key when the cached
 * value is corrupt, so the next load does not pay the same cost.
 *
 * Every storage access is wrapped: localStorage throws outright in some
 * privacy modes, and a malformed value would otherwise throw inside the effect
 * that reads it.
 */
export function readCachedCompany<T = unknown>(): T | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(SELECTED_COMPANY_CACHE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    discardCachedCompany();
    return null;
  }

  if (containsScrubSentinel(parsed)) {
    discardCachedCompany();
    return null;
  }

  return parsed as T;
}

export function discardCachedCompany(): void {
  try {
    localStorage.removeItem(SELECTED_COMPANY_CACHE_KEY);
  } catch {
    /* storage unavailable; nothing to discard */
  }
}
